import {
    CtaTypesEnum,
    NavigationButtonStyleEnum,
    PageTypesEnum, ReviewCommentsFilterEnum,
    ReviewLoginModalStateEnum, ReviewTypeEnum,
    UiItemFillTypeEnum, SocialLinkStyleEnum
} from "@/scripts/common/enums.js";
import {
    getAllTextStyles,
    getCssVarsFromColorPalette,
    getCssVarsFromColorStyles,
    getPortfolioHeroTextStyles
} from "@/scripts/common/TextStyleRenderHelper.js";
import {hexToHSL, resolveColor} from "@/scripts/common/helpers.js";
import {navigationData} from "@/scripts/common/templateDefaults/NavigationLayoutAndStyle.js";
import {
    getButtonColorMap,
    getSecondaryButtonColorMap,
    getColorPaletteWithUpdatedColorMap,
    getNavigationColorMap, getProcessStepColorMap, getSocialIconColorMap
} from "@/scripts/common/templateDefaults/colorPalettes.js";
import {
    NEW_updateRuleBasedColorGroup,
    NEW_updateSimpleColorGroup,
    updateRuleBasedColorGroup,
    updateSimpleColorGroup,
    isHexColorDark, resolveNonHexColor
} from "@/scripts/common/utilities/colorUtils.js";
import {convertColorStylesToColors} from "@/scripts/common/utilities/convertColorStylesToColors.js";
import {
    TypeAutoSectionFilledItemColorRules,
    TypeAutoSectionOutlinedItemColorRules,
    TypeAutoSectionProcessStepColorRules,
    TypeCustomSectionFilledItemColorRules,
    TypeCustomSectionOutlinedItemColorRules,
    TypeCustomSectionProcessStepColorRules
} from "@/scripts/sections/helpers/sectionColorRules.js";
import {getSiteMarginPreset} from "@/scripts/common/siteMarginPresets.js";

export const design = {
    namespaced: true,
    state() {
        return {
            fontPairs:[],
            highlightColorLayersOnUi: false
        };
    },
    getters: {
        // helpers
        editedPortfolio(state, getters, rootState) {
            return rootState.portfolio;
        },
        editedProject(state, getters, rootState) {
            return rootState.project;
        },
        templateSettings(state, getters) {
            return getters.editedPortfolio?.templateSettings;
        },
        portfolioTheme(state, getters) {
            return getters.editedPortfolio?.templateSettings?.theme;
        },
        isCaseStudy(state, getters) {
            return getters.editedProject?.type === PageTypesEnum.CASE_STUDY;
        },
        isCaseStudyWithCustomColors(state, getters) {
            return getters.isCaseStudy && !getters.projectDesignSettings.globalColorStyles;
        },
        isCaseStudyWithCustomFonts(state, getters) {
            return getters.isCaseStudy && !getters.projectDesignSettings.globalFontStyles
        },
        isCaseStudyUsingPortfolioColors(state, getters) {
            return getters.isCaseStudy && getters.projectDesignSettings.globalColorStyles;
        },
        isCaseStudyUsingPortfolioFonts(state, getters) {
            return getters.isCaseStudy && getters.projectDesignSettings.globalFontStyles;
        },
        portfolioDesignSettings(state, getters) {
            return getters.editedPortfolio?.designSettings;
        },
        projectDesignSettings(state, getters) {
            return getters.editedProject?.designSettings;
        },
        // colors
        colors(state, getters) {
            const targetSettings = getters.isCaseStudyWithCustomColors
                ? getters.projectDesignSettings
                : getters.portfolioDesignSettings;

            if (!targetSettings.colors && targetSettings.colorStyles) {
                const colors = convertColorStylesToColors(targetSettings.colorStyles);

                if (colors) {
                    targetSettings.colors = colors;
                }
            }

            return targetSettings.colors;
        },
        colorStyles(state, getters) {
            return getters.isCaseStudyWithCustomColors
                ? getters.projectDesignSettings.colorStyles
                : getters.portfolioDesignSettings.colorStyles;
        },
        colorPalette(state, getters) {
            const colorStyles = getters.colorStyles;
            const colorPalette = getters.isCaseStudyWithCustomColors
                ? getters.projectDesignSettings.colorPalette
                : getters.portfolioDesignSettings.colorPalette;

            if (colorPalette?.colors?.length > 0) {
                return colorPalette
            }

            // Default colors if colorPalette is empty or not defined
            return {
                colors: [
                    colorStyles.heroTitle.primaryColor || colorStyles.heroTitle.color,
                    colorStyles.heroParagraph.primaryColor || colorStyles.heroParagraph.color,
                    colorStyles.hyperLink.primaryColor || colorStyles.hyperLink.color,
                    colorStyles.navigation.primaryColor || colorStyles.navigation.color,
                    colorStyles.background.primaryColor || colorStyles.background.color,
                ]
            }
        },
        colorPaletteWithColorMap: (state, getters) => (colorMapItemKey, overrides = {}) => {
            const resolvedParams = {
                navItemType: overrides.navItemType ?? getters.navigationItemType,
                buttonType: overrides.buttonType ?? (getters.ctaButtonType === 'underline' ? 'outlined' : getters.ctaButtonType),
                secondaryButtonType: overrides.secondaryButtonType ?? (getters.secondaryCtaButtonType === 'underline' ? 'outlined' : getters.secondaryCtaButtonType),
                socialLinkType: overrides.socialLinkType ?? getters.socialLinkType,
                cardType: overrides.cardType ?? getters.cardType,
                labelType: overrides.labelType ?? (getters.labelStyle?.fillStyle === 'outlined' && getters.colors?.label ? 'outlined' : 'solid'),
            };
            const paletteWithUpdatedColorMap = getColorPaletteWithUpdatedColorMap(
                getters.colorPalette,
                resolvedParams
            );

            if(!colorMapItemKey) {
                return paletteWithUpdatedColorMap
            }

            return paletteWithUpdatedColorMap.COLOR_MAP[colorMapItemKey]

        },
        resolveColor: (state, getters) => (colorKey) => {
            const colorObject = getters.colorStyles?.[colorKey];
            if (!colorObject) {
                console.warn(`Color object not found for key: ${colorKey}`);
                return "#FFFFFF";
            }

            return colorObject.paletteColorIndex !== null && colorObject.paletteColorIndex !== undefined && getters.colorPalette?.colors
                ? getters.colorPalette.colors[colorObject.paletteColorIndex]
                : colorObject.primaryColor || colorObject.color;
        },

        // fonts
        fontStyles(state, getters) {
            return getters.isCaseStudyWithCustomFonts
                ? getters.projectDesignSettings.fontStyles
                : getters.portfolioDesignSettings.fontStyles;
        },
        fontPairId(state, getters) {
            return getters.isCaseStudyWithCustomFonts
                ? getters.projectDesignSettings.fontPairId
                : getters.portfolioDesignSettings.fontPairId;
        },

        // animations
        animationSettings(state, getters) {
            return getters.portfolioDesignSettings.animationSettings;
        },

        // cta style
        ctaStyle(state, getters) {
            return getters.editedPortfolio.templateSettings.ctaStyle;
        },

        secondaryCtaStyle(state, getters) {
            return getters.editedPortfolio.templateSettings.secondaryCtaStyle;
        },

        cursorType(state, getters) {
            return getters.templateSettings?.cursorType || 'DEFAULT';
        },

        ctaStyles(state, getters) {
            return {
                primary: getters.ctaStyle,
                secondary: getters.secondaryCtaStyle,
            };
        },

        labelStyle(state, getters) {
            return getters.editedPortfolio?.templateSettings?.labelStyle;
        },

        cardStyle(state, getters) {
            return getters.editedPortfolio.templateSettings?.cardStyle;
        },

        // combine text styles in to a CSS object
        getAllCssVars(state, getters) {
            const fontStyles = getters.fontStyles;
            const colorStyles = getters.colorStyles;
            const colors = getters.colors;
            const colorPalette = getters.colorPalette;
            const ctaStyles = getters.ctaStyles;
            const labelStyle = getters.labelStyle;
            const siteMargin = getSiteMarginPreset(getters.editedPortfolio?.templateSettings?.siteMargin);

            let borderColor = colorStyles.navigation;
            if (borderColor.color < 8) {
                borderColor.color += getters.portfolioTheme === 'profile_theme_default_old' ||
                (getters.portfolioTheme !== 'profile_theme_seven' &&
                    getters.portfolioTheme !== 'theme_thirteen') ? '33' : '';
            }

            let cssVars = {
                ...getAllTextStyles(fontStyles, ctaStyles, labelStyle),
                ...getCssVarsFromColorStyles(colorStyles, colors, colorPalette),
                ...getCssVarsFromColorPalette(colorPalette),
                // TODO: use getters.resolveColor instead the resolveColor helper and check if these are still valid
                '--border-color': resolveColor(borderColor, colorPalette),
                '--link-color-hover': resolveColor(colorStyles.hyperLink, colorPalette) + "CC",
                '--portfolio-site-margin': `${siteMargin.margin}px`,
                '--portfolio-site-grid-gap': `${siteMargin.gridGap}px`,
            };

            const backgroundHex = getters.resolveColor
                ? getters.resolveColor("background")
                : resolveColor(colorStyles.background, colorPalette);
            if (backgroundHex) {
                cssVars['--card-hover-mix-color'] = isHexColorDark(backgroundHex) ? '#FFFFFF' : '#000000';
            }

            // In the case of sidebar templates we need to load portfolio hero fonts and colors in the case studies too
            if(
                ['theme_nine', 'profile_theme_nine', 'theme_four', 'profile_theme_four'].includes(getters.portfolioTheme)
            ) {
                const portfolioFontStyles = getters.editedPortfolio.designSettings.fontStyles;
                const portfolioColorStyles = getters.editedPortfolio.designSettings.colorStyles;
                const portfolioColors = getters.editedPortfolio.designSettings.colors;
                const portfolioColorPalette = getters.editedPortfolio.designSettings.colorPalette?.colors?.length > 0
                    ? getters.editedPortfolio.designSettings.colorPalette
                    : {
                        id: "custom",
                        colors: [
                            portfolioColorStyles.heroTitle.primaryColor || portfolioColorStyles.heroTitle.color,
                            portfolioColorStyles.heroParagraph.primaryColor || portfolioColorStyles.heroParagraph.color,
                            portfolioColorStyles.hyperLink.primaryColor || portfolioColorStyles.hyperLink.color,
                            portfolioColorStyles.navigation.primaryColor || portfolioColorStyles.navigation.color,
                            portfolioColorStyles.background.primaryColor || portfolioColorStyles.background.color,
                        ]
                    };

                const portfolioBorderColor = resolveColor(portfolioColorStyles.navigation, portfolioColorPalette) + '33';

                // process text related styles based on portfolioStyleProperties list
                cssVars = {
                    ...cssVars,
                    ...getPortfolioHeroTextStyles(portfolioFontStyles),
                    ...getCssVarsFromColorStyles(portfolioColorStyles, portfolioColors, portfolioColorPalette, true),
                    // TODO check if these are still valid
                    '--portfolio-border-color': portfolioBorderColor,
                    '--portfolio-link-color': resolveColor(portfolioColorStyles.hyperLink, portfolioColorPalette),
                    '--portfolio-link-color-hover': resolveColor(portfolioColorStyles.hyperLink, portfolioColorPalette) + "CC",
                    '--portfolio-sidebar-background-color': resolveColor(portfolioColorStyles.sidebarBackground, portfolioColorPalette)
                };
            }

            return cssVars;
        },

        // template part helpers
        navigationItems(state, getters) {
            const sidebarThemes = ['profile_theme_default', 'profile_theme_four', 'profile_theme_nine']
            const theme = sidebarThemes.includes(getters.portfolioTheme)
                ? getters.portfolioTheme.replace("_old", '')
                : getters.portfolioTheme.replace("profile_", '').replace("_old", '');
            return getters.templateSettings.navigation?.navigationItems || navigationData[theme].navigationItems || {};
        },
        navigationItemStyle(state, getters) {
            return getters.navigationItems.button?.style;
        },
        socialLinkStyle(state, getters) {
            return getters.editedPortfolio?.templateSettings?.socialIconStyle?.fillType
                || getters.navigationItems.socialLink?.style;
        },
        navigationItemType(state, getters) {
            if (getters.navigationItemStyle === NavigationButtonStyleEnum.BOX_PILL_FILL) {
                return "filled";
            }

            if (getters.navigationItemStyle?.includes("box-")) {
                return "outlined";
            }

            return "simple";
        },
        socialLinkType(state, getters) {
            if (getters.socialLinkStyle === SocialLinkStyleEnum.SIMPLE) {
                return "simple";
            }
            
            return getters.socialLinkStyle === SocialLinkStyleEnum.BOX_PILL_FILL || getters.socialLinkStyle === UiItemFillTypeEnum.FILL
                ? "filled"
                : "outlined";
        },
        ctaButtonType(state, getters) {
            const fill = getters.ctaStyle?.fillStyle;
            if (fill === "solid") return "filled";
            if (fill === "underline") return "underline";
            return "outlined";
        },
        secondaryCtaButtonType(state, getters) {
            const fill = getters.secondaryCtaStyle?.fillStyle;
            if (fill === "solid") return "filled";
            if (fill === "underline") return "underline";
            return "outlined";
        },
        cardType(state, getters) {
            return getters.cardStyle?.cardType ?? 'none';
        },

        colorGroupsWithDifferentRulesInSection: (state, getters) => (colorOption = "auto") => {
            const exceptionsToTheCommonColorRule = {
                filled: colorOption === "auto" ? TypeAutoSectionFilledItemColorRules : TypeCustomSectionFilledItemColorRules,
                outlined: colorOption === "auto" ? TypeAutoSectionOutlinedItemColorRules : TypeCustomSectionOutlinedItemColorRules
            }

            const effectivePrimaryButtonType = getters.ctaButtonType === 'underline' ? 'outlined' : getters.ctaButtonType;
            const effectiveSecondaryButtonType = getters.secondaryCtaButtonType === 'underline' ? 'outlined' : getters.secondaryCtaButtonType;

            const navigationColorMap = getters.navigationItemType !== 'simple' ? {colorMap: getNavigationColorMap(exceptionsToTheCommonColorRule[getters.navigationItemType])} : null;
            const buttonColorMap = effectivePrimaryButtonType !== 'simple' ? {colorMap: getButtonColorMap(exceptionsToTheCommonColorRule[effectivePrimaryButtonType])} : null;
            const secondaryButtonColorMap = effectiveSecondaryButtonType !== 'simple' ? {colorMap: getSecondaryButtonColorMap(exceptionsToTheCommonColorRule[effectiveSecondaryButtonType])} : null;
            const socialIconColorMap = getters.socialLinkType !== 'simple' ? {colorMap: getSocialIconColorMap(exceptionsToTheCommonColorRule[getters.socialLinkType])} : null;
            const processStepColorMap = {colorMap: getProcessStepColorMap(colorOption === "auto" ? TypeAutoSectionProcessStepColorRules : TypeCustomSectionProcessStepColorRules)};

            return {
                navigation: navigationColorMap,
                button: buttonColorMap,
                secondaryButton:  secondaryButtonColorMap,
                socialIcon: socialIconColorMap,
                processSection: processStepColorMap
            }
        },

        highlightSectionModules: (state, getters) => (section) => {
            const shouldHighLight = state.highlightColorLayersOnUi &&
                !!section?.foregroundColorSettings &&
                typeof section.foregroundColorSettings === "object";

            if (!shouldHighLight) return null;

            const bgColor = section?.backgroundSettings?.backgroundColor;
            const color = bgColor ? resolveNonHexColor(bgColor, getters.colorPalette) : null;
            const isDark = color ? hexToHSL(color).l < 0.2 : false;

            return isDark ? "highlight-dark" : "highlight";
        },
    },
    mutations: {
        setFontPairs(state, value) {
            state.fontPairs = value;
        },
        setHighlightColorLayersOnUi(state, value) {
            state.highlightColorLayersOnUi = value;
        }
    },
    actions: {
        updateColorCluster({ state, commit, getters, dispatch }, { newColor, colorMap }) {
            let updatedColorStyles = {
                ...getters.colorStyles
            };
            
            // update legacy colors
            if (colorMap.useRules) {
                updatedColorStyles = {
                    ...updatedColorStyles,
                    ...updateRuleBasedColorGroup(
                        getters.colorStyles,
                        getters.colorPalette,
                        newColor,
                        colorMap.clusterItems,
                        colorMap.useRules
                    )
                };
            } else {
                updatedColorStyles = {
                    ...updatedColorStyles,
                    ...updateSimpleColorGroup(
                        getters.colorStyles,
                        getters.colorPalette,
                        newColor,
                        colorMap.clusterItems
                    )
                };
            }

            dispatch("updateColorStyles", updatedColorStyles);

            let updatedColors = {
                ...getters.colors
            }

            // update new colors
            if (colorMap.useRules) {
                updatedColors = {
                    ...updatedColors,
                    ...NEW_updateRuleBasedColorGroup(
                        getters.colors,
                        getters.colorPalette,
                        newColor,
                        colorMap
                    )
                };
            } else {
                updatedColors = {
                    ...updatedColors,
                    ...NEW_updateSimpleColorGroup(
                        getters.colors,
                        getters.colorPalette,
                        newColor,
                        colorMap
                    )
                };
            }

            dispatch("updateColors", updatedColors);


            commit("updateSidePanel", { allowSave: true }, { root: true });
        },
        applyColorPalette({ state, commit, getters, dispatch }) {
            Object.entries(getters.colorPaletteWithColorMap().COLOR_MAP).forEach(([groupID, colorMap]) => {
                dispatch("updateColorCluster", {
                    newColor: colorMap.colorIndex,
                    colorMap: colorMap
                })
            })
        },
        // TODO: centralize actions that follows the same pattern
        updateColorStyles({ commit, getters }, newColorStyles) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newColorStyles,
                keyToItem: getters.isCaseStudyWithCustomColors ? "project" : "portfolio",
                keyToDesignItem: "colorStyles"
            }, { root: true })
        },
        updateColors({ commit, getters }, newColors) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newColors,
                keyToItem: getters.isCaseStudyWithCustomColors ? "project" : "portfolio",
                keyToDesignItem: "colors"
            }, { root: true })
        },
        updateColorPalette({ commit, getters, rootState }, newColorPalette) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newColorPalette,
                keyToItem: getters.isCaseStudyWithCustomColors ? "project" : "portfolio",
                keyToDesignItem: "colorPalette"
            }, { root: true });
        },
        updatePreviewColorPalette({ commit, getters, rootState }, newColorPalette) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newColorPalette,
                keyToItem: "view.view.portfolio",
                keyToDesignItem: "colorPalette"
            }, { root: true });
        },
        updateFontStyles({ commit, getters }, newFontStyles) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newFontStyles,
                keyToItem: getters.isCaseStudyWithCustomFonts ? "project" : "portfolio",
                keyToDesignItem: "fontStyles"
            }, { root: true })
        },
        updateFontPairId({ commit, getters }, newFontPairId) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newFontPairId,
                keyToItem: getters.isCaseStudyWithCustomFonts ? "project" : "portfolio",
                keyToDesignItem: "fontPairId"
            }, { root: true })
        },
        updateAnimationSettings({ commit }, newAnimationSettings) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: newAnimationSettings,
                keyToItem: "portfolio",
                keyToDesignItem: "animationSettings"
            }, { root: true })
        },
        toggleGlobalSetting({ commit }, { keyToDesignItem, value }) {
            commit("UPDATE_DESIGN_SETTINGS", {
                newValue: value,
                keyToItem: "project",
                keyToDesignItem
            }, { root: true });
        },
        detachCaseStudyColors({ commit }) {
            commit("INITIALIZE_CASE_STUDY_COLORS_FROM_PORTFOLIO", null, { root: true });
        },
        detachCaseStudyFonts({ commit }) {
            commit("INITIALIZE_CASE_STUDY_FONTS_FROM_PORTFOLIO", null, { root: true });
        },
        updateLabelStyle({ commit }, newLabelStyle) {
            commit("UPDATE_LABEL_STYLE", {
                newValue: newLabelStyle,
                keyToItem: "portfolio",
                keyToObject: "templateSettings"
            }, { root: true });
            commit("updateSidePanel", { allowSave: true }, { root: true });
        },
        updateCtaStyle({ commit, getters }, { newCtaStyle, editedCtaType }) {
            const key =
                editedCtaType === CtaTypesEnum.PRIMARY ? "ctaStyle" : "secondaryCtaStyle";

            commit("UPDATE_CTA_STYLE", {
                newValue: newCtaStyle,
                editedCtaType: key,
                keyToItem: "portfolio",
                keyToObject: "templateSettings"
            }, { root: true });
            commit("updateSidePanel", { allowSave: true }, { root: true });
        },
    },
};
